import type { Meta, StoryObj } from '@storybook/react';
import { FundingNavigatorGoalCard } from './FundingNavigatorGoalCard';

const doughnutChartData = [
    { name: 'US Equity', y: 45, color: '#4169E1', subAssetClass: 'US Equity', ticker: 'SPY', tickerLink: '' },
    {
        name: "Int'l Equity",
        y: 20,
        color: '#32CD32',
        subAssetClass: 'International Equity',
        ticker: 'VXUS',
        tickerLink: ''
    },
    { name: 'Fixed Income', y: 25, color: '#FFD700', subAssetClass: 'Corporate Bonds', ticker: 'AGG', tickerLink: '' },
    { name: 'Cash', y: 10, color: '#C0C0C0', subAssetClass: 'Money Market', ticker: 'VMFXX', tickerLink: '' }
];

const groupedPortfolio = [
    { type: 'US Equity', totalAllocatedvalue: '45%', color: '#4169E1' },
    { type: "Int'l Equity", totalAllocatedvalue: '20%', color: '#32CD32' },
    { type: 'Fixed Income', totalAllocatedvalue: '25%', color: '#FFD700' },
    { type: 'Cash', totalAllocatedvalue: '10%', color: '#C0C0C0' }
];

const fundingSources = [
    {
        name: 'Brokerage Account',
        currentAllocatedBalance: 120000,
        nextContribution: 500,
        nextContributionDate: '04/01/2026'
    },
    {
        name: '401(k)',
        currentAllocatedBalance: 85000,
        nextContribution: 1200,
        nextContributionDate: '04/15/2026'
    },
    {
        name: 'IRA',
        currentAllocatedBalance: 42500,
        nextContribution: 250,
        nextContributionDate: '05/01/2026'
    }
];

const meta = {
    title: 'Organisms/FundingNavigatorGoalCard',
    component: FundingNavigatorGoalCard,
    tags: ['autodocs'],
    argTypes: {
        goalType: {
            control: 'select',
            options: ['retirement', 'wealth', 'income']
        },
        priority: {
            control: 'select',
            options: ['Need', 'Want', 'Wish', 'Dream', 'Aspire']
        },
        isOnTarget: { control: 'boolean' },
        isGoalRealistic: { control: 'boolean' },
        target: { control: { type: 'range', min: 0, max: 100, step: 1 } }
    }
} satisfies Meta<typeof FundingNavigatorGoalCard>;

export default meta;
type Story = StoryObj<typeof meta>;

export const OnTarget: Story = {
    args: {
        goalName: 'Retirement Plan',
        goalType: 'retirement',
        priority: 'Need',
        iconType: 'retirement',
        iconColor: '#4169E1',
        iconBgColor: '#E8EDFC',
        doughnutChartData,
        groupedPortfolio,
        isOnTarget: true,
        target: 85,
        fundingSources,
        isGoalRealistic: true,
        amount: 600000,
        targetDate: '2065-12-01',
        planStartRetirementDate: '2045-01-01'
    }
};

export const NeedsAttention: Story = {
    args: {
        goalName: 'Buy a House',
        goalType: 'wealth',
        priority: 'Want',
        iconType: 'buyahouse',
        iconColor: '#FF6035',
        iconBgColor: '#FFF0EB',
        doughnutChartData,
        groupedPortfolio,
        isOnTarget: false,
        target: 42,
        fundingSources: fundingSources.slice(0, 2),
        isGoalRealistic: true,
        amount: 850000,
        targetDate: '2040-12-01'
    }
};

export const Unrealistic: Story = {
    args: {
        goalName: 'Pay for College',
        goalType: 'wealth',
        priority: 'Dream',
        iconType: 'payforcollege',
        iconColor: '#DC0546',
        iconBgColor: '#FDEDF1',
        doughnutChartData,
        groupedPortfolio,
        isOnTarget: false,
        target: 12,
        fundingSources: fundingSources.slice(0, 1),
        isGoalRealistic: false,
        amount: 500000,
        targetDate: '2038-06-01'
    }
};

export const IncomeGoal: Story = {
    args: {
        goalName: 'Passive Income',
        goalType: 'income',
        priority: 'Aspire',
        iconType: 'income',
        iconColor: '#00A096',
        iconBgColor: '#E5F5F4',
        doughnutChartData,
        groupedPortfolio,
        isOnTarget: true,
        target: 78,
        fundingSources,
        isGoalRealistic: true,
        amount: 300000,
        targetDate: '2055-12-01',
        planStartRetirementDate: '2040-01-01'
    }
};

export const SingleFundingSource: Story = {
    args: {
        goalName: 'Buy a Car',
        goalType: 'wealth',
        priority: 'Wish',
        iconType: 'buyacar',
        iconColor: '#FFD700',
        iconBgColor: '#FFFBE5',
        doughnutChartData: doughnutChartData.slice(0, 2),
        groupedPortfolio: groupedPortfolio.slice(0, 2),
        isOnTarget: true,
        target: 91,
        fundingSources: [fundingSources[0]],
        isGoalRealistic: true,
        amount: 200000,
        targetDate: '2035-03-01'
    }
};
